import React, { useState, useMemo } from "react";
import { Users, Mail, Calendar, DollarSign, Crown, Shield, User, Search, Settings, Percent, Gift, Trash2, X, AlertTriangle, CreditCard } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SubscriptionModal from "./SubscriptionModal";
import { adminSubscriptionService } from "../services/adminSubscriptionService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface UserInterface {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  last_login?: string;
  next_debit?: string;
  total_spent?: number;
  total_refunds?: number;
  avatar?: string;
  subscription: {
    plan: "free" | "premium" | "pro";
    status: "active" | "canceled" | "expired" | "past_due" | "unpaid";
    billing: "monthly" | "yearly";
    expiresAt: string;
    renewsAt?: string;
    cancelAt?: string;
    features: string[];
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    priceId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    discount?: {
      code: string;
      percentage: number;
      amount?: number;
      expiresAt?: string;
      description?: string;
    };
  } | null;
}

interface UserListProps {
  users: UserInterface[];
  onUserUpdate?: (updatedUser: any) => void;
}

const UserList: React.FC<UserListProps> = ({ users, onUserUpdate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserInterface | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [discountProcessing, setDiscountProcessing] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [resetPasswordProcessing, setResetPasswordProcessing] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    code: "",
    percentage: "",
    amount: "",
    type: "percentage" as "percentage" | "fixed",
    expiresAt: "",
    description: "",
  });
  const [refundForm, setRefundForm] = useState({
    amount: "",
    reason: "",
    type: "full" as "full" | "partial",
  });

  // Filter users based on search term (name, email, or ID)
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }

    const search = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email.toLowerCase();
      const id = String(user.id).toLowerCase();
      
      return fullName.includes(search) || 
             email.includes(search) || 
             id.includes(search);
    });
  }, [users, searchTerm]);

  // Calculate financial data for charts
  const financialData = useMemo(() => {
    const planData = {
      free: { revenue: 0, refunds: 0, discounts: 0, users: 0 },
      pro: { revenue: 0, refunds: 0, discounts: 0, users: 0 },
      premium: { revenue: 0, refunds: 0, discounts: 0, users: 0 }
    };

    users.forEach(user => {
      const plan = user.subscription?.plan || 'free';
      const totalSpent = user.total_spent || 0;
      const totalRefunds = user.total_refunds || 0;
      
      // Calculate discount amount
      let discountAmount = 0;
      if (user.subscription?.discount) {
        const discount = user.subscription.discount;
        if (discount.percentage > 0) {
          // For percentage discounts, calculate based on the user's plan price
          const plan = user.subscription.plan;
          const billing = user.subscription.billing || 'monthly';
          let planPrice = 0;
          
          if (plan === 'pro') {
            planPrice = billing === 'yearly' ? 0.99 : 9.99;
          } else if (plan === 'premium') {
            planPrice = billing === 'yearly' ? 2.99 : 29.99;
          }
          
          discountAmount = planPrice * (discount.percentage / 100);
        } else if (discount.amount && discount.amount > 0) {
          discountAmount = discount.amount;
        }
      }
      
      planData[plan as keyof typeof planData].users += 1;
      planData[plan as keyof typeof planData].revenue += totalSpent;
      planData[plan as keyof typeof planData].refunds += totalRefunds;
      planData[plan as keyof typeof planData].discounts += discountAmount;
    });

    return planData;
  }, [users]);



  const handleManageSubscription = (user: UserInterface) => {
    setSelectedUser(user);
    setShowSubscriptionModal(true);
  };

  const handleManageDiscount = (user: UserInterface) => {
    setSelectedUser(user);
    // Reset form
    setDiscountForm({
      code: "",
      percentage: "",
      amount: "",
      type: "percentage",
      expiresAt: "",
      description: "",
    });
    setShowDiscountModal(true);
  };

  const handleManageRefund = (user: UserInterface) => {
    setSelectedUser(user);
    // Reset form
    setRefundForm({
      amount: "",
      reason: "",
      type: "full",
    });
    setShowRefundModal(true);
  };

  const handleSubscriptionSuccess = (updatedUser: any) => {
    if (onUserUpdate) {
      onUserUpdate(updatedUser);
    }
    setShowSubscriptionModal(false);
    setSelectedUser(null);
  };

  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSelectedUser(null);
  };

  const closeDiscountModal = () => {
    setShowDiscountModal(false);
    setSelectedUser(null);
    setDiscountForm({
      code: "",
      percentage: "",
      amount: "",
      type: "percentage",
      expiresAt: "",
      description: "",
    });
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setSelectedUser(null);
    setRefundForm({
      amount: "",
      reason: "",
      type: "full",
    });
  };

  const handleDiscountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setDiscountProcessing(true);

    try {
      const discountValue = discountForm.type === "percentage" 
        ? parseFloat(discountForm.percentage) 
        : parseFloat(discountForm.amount);

      if (isNaN(discountValue) || discountValue <= 0) {
        toast.error("Please enter a valid discount value");
        setDiscountProcessing(false);
        return;
      }

      if (discountForm.type === "percentage" && discountValue > 100) {
        toast.error("Percentage discount cannot exceed 100%");
        setDiscountProcessing(false);
        return;
      }

      const response = await adminSubscriptionService.applyUserDiscount({
        user_id: selectedUser.id,
        discount_code: discountForm.code || undefined,
        discount_type: discountForm.type,
        discount_value: discountValue,
        expires_at: discountForm.expiresAt || undefined,
        description: discountForm.description || undefined,
        notify_user: true,
      });

      if (response.success && response.user) {
        if (onUserUpdate) {
          onUserUpdate(response.user);
        }
        toast.success(`Discount successfully applied to ${selectedUser.first_name} ${selectedUser.last_name}!`);
        closeDiscountModal();
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      console.error("Error applying discount:", error);
      toast.error(`Failed to apply discount: ${error.message || "Please try again"}`);
    } finally {
      setDiscountProcessing(false);
    }
  };

  const handleRemoveDiscount = async (user: UserInterface) => {
    if (!window.confirm(`Are you sure you want to remove the discount from ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      const response = await adminSubscriptionService.removeUserDiscount(user.id);
      
      if (response.success && response.user) {
        if (onUserUpdate) {
          onUserUpdate(response.user);
        }
        toast.success(`Discount successfully removed from ${user.first_name} ${user.last_name}!`);
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      console.error("Error removing discount:", error);
      toast.error(`Failed to remove discount: ${error.message || "Please try again"}`);
    }
  };

  const handleResetPassword = async (user: UserInterface) => {
    setResetPasswordProcessing(true);

    try {
      const response = await adminSubscriptionService.resetUserPassword(user.id, user.email);
      
      if (response.success) {
        toast.success(`Password reset email sent to ${user.first_name} ${user.last_name} (${user.email})`);
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      console.error("Error sending password reset:", error);
      toast.error(`Failed to send password reset: ${error.message || "Please try again"}`);
    } finally {
      setResetPasswordProcessing(false);
    }
  };

  const handleDeleteUser = async (user: UserInterface) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
    setDeleteProcessing(false);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) {
      return;
    }

    setDeleteProcessing(true);

    try {
      const response = await adminSubscriptionService.deleteUser(selectedUser.id);
      
      if (response.success) {
        // Remove user from the local list
        if (onUserUpdate) {
          // Signal to parent component to refresh the user list
          onUserUpdate({ action: 'delete', userId: selectedUser.id });
        }
        toast.success(`User account for ${selectedUser.first_name} ${selectedUser.last_name} has been permanently deleted.`);
        closeDeleteModal();
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error(`Failed to delete user: ${error.message || "Please try again"}`);
    } finally {
      setDeleteProcessing(false);
    }
  };

  const handleRefundSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setRefundProcessing(true);

    try {
      if (!refundForm.reason.trim()) {
        toast.error("Please provide a reason for the refund");
        setRefundProcessing(false);
        return;
      }

      if (refundForm.type === "partial") {
        const amount = parseFloat(refundForm.amount);
        if (isNaN(amount) || amount <= 0) {
          toast.error("Please enter a valid refund amount");
          setRefundProcessing(false);
          return;
        }

        if (selectedUser.total_spent && amount > selectedUser.total_spent) {
          toast.error("Refund amount cannot exceed total spent amount");
          setRefundProcessing(false);
          return;
        }
      }

      const response = await adminSubscriptionService.processUserRefund({
        user_id: selectedUser.id,
        amount: refundForm.type === "partial" ? parseFloat(refundForm.amount) : undefined,
        reason: refundForm.reason,
        refund_type: refundForm.type,
        notify_user: true,
      });

      if (response.success && response.user) {
        if (onUserUpdate) {
          onUserUpdate(response.user);
        }
        toast.success(`Refund successfully processed for ${selectedUser.first_name} ${selectedUser.last_name}!`);
        closeRefundModal();
      } else {
        throw new Error(response.error || "Unknown error occurred");
      }
    } catch (error: any) {
      console.error("Error processing refund:", error);
      toast.error(`Failed to process refund: ${error.message || "Please try again"}`);
    } finally {
      setRefundProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPlanBadge = (plan: string | undefined) => {
    switch (plan) {
      case "premium":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
            <Crown className="h-3 w-3 mr-1" />
            Premium
          </span>
        );
      case "pro":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            <Shield className="h-3 w-3 mr-1" />
            Pro
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <User className="h-3 w-3 mr-1" />
            Free
          </span>
        );
    }
  };

  const getSubscriptionStatus = (status: string | undefined) => {
    switch (status) {
      case "active":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            Active
          </span>
        );
      case "canceled":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            Canceled
          </span>
        );
      case "expired":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
            Expired
          </span>
        );
      case "past_due":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-500 text-white">
            Past Due
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            N/A
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Users className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Manage and monitor all registered users</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{filteredUsers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-gray-100 text-gray-600 mr-4">
              <User className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Free Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredUsers.filter(u => (u.subscription?.plan || 'free') === 'free').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pro Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredUsers.filter(u => u.subscription?.plan === "pro").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-pink-100 text-pink-600 mr-4">
              <Crown className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Premium Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredUsers.filter(u => u.subscription?.plan === "premium").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Plan Performance</h3>
          <p className="text-sm text-gray-600 mt-1">Detailed financial breakdown by subscription plan</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue (Credits)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Refunds (Debits)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discounts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg. per User
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <User className="h-3 w-3 mr-1" />
                    Free
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {financialData.free.users}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                  ${financialData.free.revenue.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                  ${financialData.free.refunds.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-medium">
                  ${financialData.free.discounts.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${financialData.free.users > 0 ? (financialData.free.revenue / financialData.free.users).toFixed(2) : '0.00'}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    <Shield className="h-3 w-3 mr-1" />
                    Pro
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {financialData.pro.users}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                  ${financialData.pro.revenue.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                  ${financialData.pro.refunds.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-medium">
                  ${financialData.pro.discounts.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${financialData.pro.users > 0 ? (financialData.pro.revenue / financialData.pro.users).toFixed(2) : '0.00'}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                    <Crown className="h-3 w-3 mr-1" />
                    Premium
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {financialData.premium.users}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                  ${financialData.premium.revenue.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                  ${financialData.premium.refunds.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-medium">
                  ${financialData.premium.discounts.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${financialData.premium.users > 0 ? (financialData.premium.revenue / financialData.premium.users).toFixed(2) : '0.00'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="max-w-md">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
            Search Users
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="search"
              placeholder="Search by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
            />
          </div>
          {searchTerm && (
            <p className="mt-2 text-sm text-gray-600">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} matching "{searchTerm}"
            </p>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
          <p className="text-sm text-gray-600 mt-1">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Debit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {user.avatar ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={user.avatar}
                            alt={`${user.first_name} ${user.last_name}`}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                      <div className="text-sm text-gray-900">
                        {formatDate(user.created_at)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.last_login ? formatDate(user.last_login) : "Never"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {user.next_debit ? formatDate(user.next_debit) : "Not set"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getPlanBadge(user.subscription?.plan)}
                      {user.subscription && (
                        <div>{getSubscriptionStatus(user.subscription.status)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.subscription?.discount ? (
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Percent className="h-3 w-3 mr-1" />
                            {user.subscription.discount.percentage > 0 
                              ? `${user.subscription.discount.percentage}%` 
                              : `$${user.subscription.discount.amount}`}
                          </span>
                          <button
                            onClick={() => handleRemoveDiscount(user)}
                            className="text-red-600 hover:text-red-800 text-xs"
                            title="Remove discount"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="text-xs text-gray-500">
                          Code: {user.subscription.discount.code}
                        </div>
                        {user.subscription.discount.expiresAt && (
                          <div className="text-xs text-orange-600">
                            Expires: {new Date(user.subscription.discount.expiresAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">No discount</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                      <div className="text-sm font-medium text-gray-900">
                        ${user.total_spent?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleManageSubscription(user)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <Settings className="h-4 w-4 mr-1" />
                        Manage Plan
                      </button>
                      <button
                        onClick={() => handleManageDiscount(user)}
                        className="inline-flex items-center px-3 py-1.5 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <Gift className="h-4 w-4 mr-1" />
                        Discount
                      </button>
                      {/* Show Refund button active for paid users with spending, inactive for free users or users with no spending */}
                      {user.subscription && user.subscription.plan !== "free" && user.total_spent && user.total_spent > 0 ? (
                        <button
                          onClick={() => handleManageRefund(user)}
                          className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Refund
                        </button>
                      ) : (
                        <button
                          disabled
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-400 bg-gray-100 cursor-not-allowed opacity-50"
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Refund
                        </button>
                      )}
                      <button
                        onClick={() => handleResetPassword(user)}
                        disabled={resetPasswordProcessing}
                        className="inline-flex items-center px-3 py-1.5 border border-purple-300 shadow-sm text-xs font-medium rounded text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        {resetPasswordProcessing ? 'Sending...' : 'Reset Password'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subscription Modal */}
      {selectedUser && (
        <SubscriptionModal
          user={selectedUser}
          isOpen={showSubscriptionModal}
          onClose={closeSubscriptionModal}
          onSuccess={handleSubscriptionSuccess}
        />
      )}

      {/* Discount Modal */}
      {selectedUser && showDiscountModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <Gift className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Apply Discount
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDiscountModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Current discount info */}
              {selectedUser.subscription?.discount && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Current Discount</h4>
                  <p className="text-sm text-blue-700">
                    Code: {selectedUser.subscription.discount.code} - 
                    {selectedUser.subscription.discount.percentage > 0 
                      ? ` ${selectedUser.subscription.discount.percentage}% off`
                      : ` $${selectedUser.subscription.discount.amount} off`}
                  </p>
                  {selectedUser.subscription.discount.description && (
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedUser.subscription.discount.description}
                    </p>
                  )}
                </div>
              )}

              {/* Discount Form */}
              <form onSubmit={handleDiscountSubmit} className="space-y-4">
                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type
                  </label>
                  <select
                    value={discountForm.type}
                    onChange={(e) => setDiscountForm({...discountForm, type: e.target.value as "percentage" | "fixed"})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {discountForm.type === "percentage" ? "Percentage (1-100)" : "Amount ($)"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={discountForm.type === "percentage" ? "100" : undefined}
                    step={discountForm.type === "percentage" ? "1" : "0.01"}
                    value={discountForm.type === "percentage" ? discountForm.percentage : discountForm.amount}
                    onChange={(e) => setDiscountForm({
                      ...discountForm,
                      [discountForm.type === "percentage" ? "percentage" : "amount"]: e.target.value
                    })}
                    placeholder={discountForm.type === "percentage" ? "e.g., 20" : "e.g., 5.00"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                {/* Discount Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={discountForm.code}
                    onChange={(e) => setDiscountForm({...discountForm, code: e.target.value})}
                    placeholder="e.g., SAVE20 (auto-generated if empty)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Expiration Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires On (Optional)
                  </label>
                  <input
                    type="date"
                    value={discountForm.expiresAt}
                    onChange={(e) => setDiscountForm({...discountForm, expiresAt: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={discountForm.description}
                    onChange={(e) => setDiscountForm({...discountForm, description: e.target.value})}
                    placeholder="Internal note about this discount..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeDiscountModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={discountProcessing}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {discountProcessing ? "Applying..." : "Apply Discount"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {selectedUser && showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Delete User Account
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDeleteModal}
                  disabled={deleteProcessing}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Warning Content */}
              <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm">
                    <h4 className="font-medium text-red-800 mb-2">⚠️ WARNING: This action cannot be undone!</h4>
                    <p className="text-red-700 mb-2">
                      Are you absolutely sure you want to permanently delete the account for:
                    </p>
                    <p className="font-medium text-red-800 mb-2">
                      {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})
                    </p>
                    <p className="text-red-700 mb-2">This will:</p>
                    <ul className="list-disc list-inside text-red-700 space-y-1">
                      <li>Delete all user data permanently</li>
                      <li>Cancel any active subscriptions</li>
                      <li>Remove all recipes and meal plans</li>
                      <li>Cannot be reversed</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleteProcessing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={deleteProcessing}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteProcessing ? "Deleting..." : "Delete User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {selectedUser && showRefundModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                    <CreditCard className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Process Refund
                    </h3>
                    <p className="text-sm text-gray-500">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeRefundModal}
                  disabled={refundProcessing}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* User info */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-1">User Information</h4>
                <p className="text-sm text-blue-700">
                  Email: {selectedUser.email}
                </p>
                <p className="text-sm text-blue-700">
                  Total Spent: ${selectedUser.total_spent?.toFixed(2) || "0.00"}
                </p>
                {selectedUser.subscription && (
                  <p className="text-sm text-blue-700">
                    Current Plan: {selectedUser.subscription.plan} ({selectedUser.subscription.status})
                  </p>
                )}
              </div>

              {/* Refund Form */}
              <form onSubmit={handleRefundSubmit} className="space-y-4">
                {/* Refund Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refund Type
                  </label>
                  <select
                    value={refundForm.type}
                    onChange={(e) => setRefundForm({...refundForm, type: e.target.value as "full" | "partial"})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="full">Full Refund</option>
                    <option value="partial">Partial Refund</option>
                  </select>
                </div>

                {/* Refund Amount - Only show for partial refunds */}
                {refundForm.type === "partial" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refund Amount ($)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      max={selectedUser.total_spent || 0}
                      step="0.01"
                      value={refundForm.amount}
                      onChange={(e) => setRefundForm({...refundForm, amount: e.target.value})}
                      placeholder="e.g., 25.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum refund amount: ${selectedUser.total_spent?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refund Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm({...refundForm, reason: e.target.value})}
                    placeholder="Please provide a detailed reason for this refund..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Warning */}
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-sm">
                      <h4 className="font-medium text-yellow-800 mb-1">Important Notice</h4>
                      <p className="text-yellow-700">
                        This action will process a refund through the payment gateway. 
                        The user will be notified and their subscription may be affected.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeRefundModal}
                    disabled={refundProcessing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={refundProcessing}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {refundProcessing ? "Processing..." : "Process Refund"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default UserList;
